import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtUSDT, fmtINR } from "@/components/common";
import Uploader from "@/components/Uploader";

const UPI_RE = /^[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}$/;

export default function SellUsdt() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [settings, setSettings] = useState({});
  const [pid, setPid] = useState("");
  const [amount, setAmount] = useState("");
  const [upi, setUpi] = useState("");
  const [upiName, setUpiName] = useState("");
  const [qrFileId, setQrFileId] = useState(null);
  const [bankProofId, setBankProofId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBank, setShowBank] = useState(false);
  const [bank, setBank] = useState({ holder: "", bank_name: "", account_number: "", ifsc: "", branch: "", account_type: "Savings" });

  useEffect(() => {
    api.get("/products").then((r) => { setProducts(r.data); if (r.data[0]) setPid(r.data[0].id); }).catch(() => {});
    api.get("/customer/wallet").then((r) => setWallet(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const product = products.find((p) => p.id === pid);
  const rate = product ? parseFloat(product.sell_rate) : 0;
  const fee = product ? parseFloat(product.fee_percent || 0) : 0;
  const amt = parseFloat(amount || 0);
  const gross = amt * rate;
  const feeInr = gross * fee / 100;
  const expected = gross - feeInr;
  const deficit = wallet && parseFloat(wallet.deficit) > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!showBank) {
      if (!UPI_RE.test(upi.trim())) { toast.error("Enter a valid UPI ID (e.g. name@bank)"); return; }
      if (!upiName.trim()) { toast.error("UPI / account holder name is required"); return; }
      if (settings.require_qr && !qrFileId) { toast.error("A UPI QR code image is required"); return; }
    }
    if (!bankProofId) { toast.error("Upload Latest Bank Transaction Screenshot is required"); return; }
    setLoading(true);
    try {
      const payload = {
        product_id: pid, amount,
        upi_id: showBank ? null : upi, upi_name: showBank ? null : upiName,
        upi_qr_file_id: showBank ? null : qrFileId, bank: showBank ? bank : null,
        bank_proof_file_id: bankProofId,
      };
      const { data } = await api.post("/customer/orders", payload);
      toast.success(`Order ${data.order_id} created — ${fmtUSDT(amount)} USDT reserved`);
      nav(`/app/orders/${data.order_id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">Sell USDT</h1>
        <p className="text-sm text-slate-500 mt-1">Create a sell order at a locked rate. The USDT is reserved instantly.</p>
      </div>

      {wallet && (
        <div className="flex gap-4 text-sm">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-2" data-testid="sell-available-balance">
            Available: <span className="font-mono font-bold text-blue-700">{fmtUSDT(wallet.available)}</span> USDT
          </div>
        </div>
      )}

      {deficit && (
        <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-xl p-4 text-rose-800 text-sm" data-testid="sell-deficit-block">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          New sell orders are blocked while you have a deficit of {fmtUSDT(wallet.deficit)} USDT. Add a deposit to clear it.
        </div>
      )}

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Product</span>
          <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="sell-product-select"
            className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-blue-500 outline-none bg-white">
            {products.map((p) => <option key={p.id} value={p.id}>{p.name} — {fmtINR(p.sell_rate)}/USDT</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">USDT amount to sell</span>
          <input type="number" step="0.000001" min="0" required value={amount} onChange={(e) => setAmount(e.target.value)}
            data-testid="sell-amount-input" className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-blue-500 outline-none font-mono text-lg" placeholder="0.00" />
          {product && (parseFloat(product.min_amount) > 0 || parseFloat(product.max_amount) > 0) && (
            <span className="text-xs text-slate-400 mt-1 block">Limits: {fmtUSDT(product.min_amount)} – {fmtUSDT(product.max_amount)} USDT</span>
          )}
        </label>

        <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm" data-testid="sell-breakdown">
          <Row label="USDT amount" value={`${fmtUSDT(amt)} USDT`} />
          <Row label="Applicable rate" value={`${fmtINR(rate)} / USDT`} />
          <Row label="Gross INR" value={fmtINR(gross)} />
          <Row label={`Fee (${fee}%)`} value={`− ${fmtINR(feeInr)}`} />
          <div className="border-t border-slate-200 pt-2 flex justify-between font-bold text-slate-900">
            <span>Expected INR</span><span className="font-mono text-emerald-700" data-testid="sell-expected-inr">{fmtINR(expected)}</span>
          </div>
        </div>

        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
          <button type="button" onClick={() => setShowBank(false)} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${!showBank ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`} data-testid="payout-upi-tab">UPI</button>
          <button type="button" onClick={() => setShowBank(true)} className={`flex-1 py-2 rounded-lg text-sm font-semibold ${showBank ? "bg-white text-blue-700 shadow-sm" : "text-slate-500"}`} data-testid="payout-bank-tab">Bank transfer</button>
        </div>

        {!showBank ? (
          <div className="space-y-3 border border-slate-200 rounded-xl p-4">
            <div className="text-sm font-semibold text-slate-800">UPI Details</div>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">UPI ID (required)</span>
              <input value={upi} onChange={(e) => setUpi(e.target.value)} data-testid="sell-upi-input"
                className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-blue-500 outline-none" placeholder="name@bank" />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-slate-700">UPI / Account holder name (required)</span>
              <input value={upiName} onChange={(e) => setUpiName(e.target.value)} data-testid="sell-upi-name-input"
                className="mt-1.5 w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-blue-500 outline-none" placeholder="Full name" />
            </label>
            <div>
              <span className="text-sm font-medium text-slate-700">UPI QR code {settings.require_qr ? "(required)" : "(optional)"}</span>
              <div className="mt-1.5"><Uploader fileId={qrFileId} onUploaded={setQrFileId} testid="sell-upi-qr-upload" label="Upload UPI QR" /></div>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {[["holder", "Account holder name"], ["bank_name", "Bank name"], ["account_number", "Account number"], ["ifsc", "IFSC"], ["branch", "Branch"]].map(([k, label]) => (
              <label key={k} className="block">
                <span className="text-sm font-medium text-slate-700">{label}</span>
                <input required={showBank} value={bank[k]} onChange={(e) => setBank({ ...bank, [k]: e.target.value })}
                  data-testid={`sell-bank-${k}`} className="mt-1.5 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm" />
              </label>
            ))}
            <label className="block">
              <span className="text-sm font-medium text-slate-700">Account type</span>
              <select value={bank.account_type} onChange={(e) => setBank({ ...bank, account_type: e.target.value })}
                className="mt-1.5 w-full px-3 py-2 rounded-xl border border-slate-300 outline-none text-sm bg-white">
                <option>Savings</option><option>Current</option>
              </select>
            </label>
          </div>
        )}

        <div className="border border-slate-200 rounded-xl p-4 space-y-3" data-testid="bank-verification-section">
          <div className="text-sm font-semibold text-slate-800">Bank Account Verification</div>
          <p className="text-xs text-slate-500 leading-relaxed">
            To verify that your bank account is active and working, please make a small transaction from this account and upload a screenshot of the latest successful transaction.
            The screenshot should clearly show the transaction status, date and time, and transaction reference number. Please hide your account balance and any sensitive personal information.
          </p>
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
            Note: Uploading a screenshot does not automatically verify your account. Verification is subject to review.
          </p>
          <div>
            <span className="text-sm font-medium text-slate-700">Upload Latest Bank Transaction Screenshot <span className="text-rose-600">*</span></span>
            <div className="mt-1.5"><Uploader fileId={bankProofId} onUploaded={setBankProofId} testid="bank-proof-upload" label="Upload transaction screenshot" /></div>
          </div>
        </div>

        <button disabled={loading || deficit} data-testid="sell-submit-button"
          className="w-full px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition disabled:opacity-60 inline-flex items-center justify-center gap-2">
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create sell order
        </button>
      </form>
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-slate-600"><span>{label}</span><span className="font-mono text-slate-900">{value}</span></div>;
}
